import React from 'react';
import { Heading, Paragraph, Section } from '@piggybank/core';
import {
    Field,
    Legend,
    Fieldset,
    TelephoneInput,
    Label,
    Select,
    TextInput,
    YesNoRadio,
    AddAnother,
    DateInput,
    FieldFeedback,
} from '@piggybank/form';
import i18n from 'i18next';
import { CountryOfPhone, Gender, IdType } from 'referenceData';
import { linkTagging } from 'tealiumTrack';
import { CHINA, PASSPORT, EEP, OTHERS, NO, YES } from '../../../referenceData/constants';

const COMMON_SELECT = 'ICO.COMMON.SELECT';
const DATE_OF_BIRTH = 'ICO.PERSONAL.DATE_OF_BIRTH';
const DATE_OF_EXPIRY = 'ICO.PERSONAL.DATE_OF_EXPIRY';
const TAG_PAGE_URL = '/forms/personal-information';
const TAG_PAGE_NAME = 'pws:forms:personal information';

export const getOptionLabelByValue = (options, value) => {
    if (value === OTHERS) {
        return i18n.t('ICO.PERSONAL.PERMIT_TYPE_OTHERS_DESC');
    }

    const filterData = options?.find((option) => option.value === value);

    return filterData?.label ?? '';
};

export const getOtherNationalityOptions = (countryList, nationality) => {
    if (nationality !== CHINA) {
        return countryList.filter((option) => option.value !== CHINA);
    }

    return countryList;
};

export const getTitleOptionsByGender = (titleOptions, gender) => titleOptions.filter((option) => (
    gender !== 'Male' ? option.value !== 'Mr' : option.value === 'Mr' || option.value === 'Others'
));

const getTrailingNumber = (value) => parseInt(value.slice(-1), 10);
export const getOtherNationalityFieldName = (path) => `nationality${getTrailingNumber(path) + 2}`;

export const ContactDetailsSection = ({ t, lan, values }) => (
    <>
        <Heading level={2} accentBar>{t('ICO.PERSONAL.CONTACT_DETAILS')}</Heading>
        <Field marginBottom={5} name="email">
            <Label marginBottom={1}>{t('ICO.PERSONAL.EMAIL_ADDRESS')}</Label>
            <Paragraph id="email_address_desc" hint>{t('ICO.PERSONAL.EMAIL_ADDRESS_DESC')}</Paragraph>
            <TextInput aria-describedby="email_address_desc" fullWidth />
            <FieldFeedback />
        </Field>
        <Field marginBottom={5} name="phoneNumber">
            <Label marginBottom={1}>{t('ICO.PERSONAL.PHONE_NUMBER')}</Label>
            <Paragraph id="phone_number_desc" hint>{t('ICO.PERSONAL.PHONE_NUMBER_DESC')}</Paragraph>
            <TelephoneInput
                id="phoneNumber-field"
                aria-describedby="phone_number_desc"
                countryCode="CN"
                maxLength={values.phoneNumber?.countryCode === 'CN' ? 11 : 20}
                extendedValues
                locale={lan !== 'en' ? 'zh' : lan}
                textMap={{
                    selectCountry: 'Select area code',
                    countryList: CountryOfPhone[lan]
                }}
                fullWidth />
            <FieldFeedback />
        </Field>
    </>
);

export const AboutYouSection = ({ t, lan, values, titleOptions, countryList, setFieldValue }) => {
    const filteredTitleOptions = getTitleOptionsByGender(titleOptions, values.gender);

    return (
        <Section>
            <Heading level={2} accentBar>{t('ICO.PERSONAL.ABOUT_YOU')}</Heading>
            <Field name="gender">
                <Label>{t('ICO.PERSONAL.GENDER')}</Label>
                <Select placeholder={t(COMMON_SELECT)} options={Gender[lan]} fullWidth />
                <FieldFeedback />
            </Field>
            <Field name="title">
                <Label>{t('ICO.PERSONAL.ABOUT_YOU_TITLE')}</Label>
                <Select placeholder={t(COMMON_SELECT)} options={filteredTitleOptions} fullWidth />
                <FieldFeedback />
            </Field>
            <Field
                name='firstName'
                onBlur={(next, { value, ...rest }) => {
                    setFieldValue('fullName', `${values.lastName}${value}`);
                    next({ value, ...rest });
                }}>
                <Label>{t('ICO.PERSONAL.FIRST_NAME')}</Label>
                <Paragraph id="first_name_desc" hint>{t('ICO.PERSONAL.FIRST_NAME_DESC')}</Paragraph>
                <TextInput aria-describedby="first_name_desc" fullWidth />
                <FieldFeedback />
            </Field>
            <Field
                name='lastName'
                onBlur={(next, { value, ...rest }) => {
                    setFieldValue('fullName', `${value}${values.firstName}`);
                    next({ value, ...rest });
                }}>
                <Label>{t('ICO.PERSONAL.LAST_NAME')}</Label>
                <Paragraph id="last_name_desc" hint>{t('ICO.PERSONAL.LAST_NAME_DESC')}</Paragraph>
                <TextInput aria-describedby="last_name_desc" fullWidth />
                <FieldFeedback />
            </Field>
            <Field name="dateOfBirth" marginBottom={5}>
                <Legend>{t(DATE_OF_BIRTH)}</Legend>
                <Paragraph hint>{t('ICO.PERSONAL.DATE_OF_BIRTH_DESC')}</Paragraph>
                <DateInput
                    textMap={{
                        dayAriaLabel: `${t(DATE_OF_BIRTH)} ${t('ICO.PERSONAL.DATE_OF_BIRTH_DAY')}`,
                        monthAriaLabel: `${t(DATE_OF_BIRTH)} ${t('ICO.PERSONAL.DATE_OF_BIRTH_MONTH')}`,
                        yearAriaLabel: `${t(DATE_OF_BIRTH)} ${t('ICO.PERSONAL.DATE_OF_BIRTH_YEAR')}`,
                        day: t('ICO.PERSONAL.DATE_OF_BIRTH_DAY'),
                        month: t('ICO.PERSONAL.DATE_OF_BIRTH_MONTH'),
                        year: t('ICO.PERSONAL.DATE_OF_BIRTH_YEAR')
                    }}
                />
                <FieldFeedback />
            </Field>
            <Field name="countryOfBirth">
                <Label>{t('ICO.PERSONAL.COUNTRY_OF_BIRTH')}</Label>
                <Select
                    placeholder={t('ICO.PERSONAL.COUNTRY_OF_BIRTH_PLACEHOLDER')}
                    options={countryList}
                    fullWidth
                />
                <FieldFeedback />
            </Field>
        </Section>
    );
};

export const IdentityDetailsSection = ({
    t,
    lan,
    lang,
    values,
    countryList,
    setFieldValue,
    validateField,
    onDocumentTypeChange,
    onNationalityChange,
    onOtherNationalityAdd,
    onOtherNationalityRemove,
}) => (
    <Section>
        <Heading level={2} accentBar>{t('ICO.PERSONAL.IDENTITY_DETAILS')}</Heading>
        <Field
            name="documentType"
            onChange={(next, { value, ...rest }) => {
                onDocumentTypeChange(value, setFieldValue);
                next({ value, ...rest });
                Promise.resolve().then(() => validateField('permitNumber'));
            }}>
            <Label>{t('ICO.PERSONAL.PERMIT_TYPE')}</Label>
            <Paragraph hint>{t('ICO.PERSONAL.PERMIT_TYPE_DESC')}</Paragraph>
            <Select placeholder={t(COMMON_SELECT)} options={IdType[lan]} fullWidth />
            <FieldFeedback />
        </Field>
        {values.documentType === PASSPORT &&
            <Field name="placeOfIssue">
                <Label>{t('ICO.PERSONAL.PERMIT_COUNTRY')}</Label>
                <Select
                    placeholder={t('ICO.PERSONAL.COUNTRY_OF_BIRTH_PLACEHOLDER')}
                    options={countryList}
                    fullWidth
                />
                <FieldFeedback />
            </Field>
        }
        {values.documentType && <>
            <Field name="permitNumber">
                <Label marginBottom={1}>{t('ICO.PERSONAL.PERMIT_NUMBER')}</Label>
                <Paragraph id="permit_number_desc" hint>
                    {t('ICO.PERSONAL.PERMIT_NUMBER_DESC', { documentType: getOptionLabelByValue(IdType[lan], values.documentType) })}
                </Paragraph>
                <TextInput aria-describedby="permit_number_desc" fullWidth />
                <FieldFeedback />
            </Field>
            <Fieldset name="dateOfExpiry">
                <Legend>{t(DATE_OF_EXPIRY)}</Legend>
                <DateInput
                    textMap={{
                        dayAriaLabel: `${t(DATE_OF_EXPIRY)} ${t('ICO.PERSONAL.DATE_OF_BIRTH_DAY')}`,
                        monthAriaLabel: `${t(DATE_OF_EXPIRY)} ${t('ICO.PERSONAL.DATE_OF_BIRTH_MONTH')}`,
                        yearAriaLabel: `${t(DATE_OF_EXPIRY)} ${t('ICO.PERSONAL.DATE_OF_BIRTH_YEAR')}`,
                        day: t('ICO.PERSONAL.DATE_OF_BIRTH_DAY'),
                        month: t('ICO.PERSONAL.DATE_OF_BIRTH_MONTH'),
                        year: t('ICO.PERSONAL.DATE_OF_BIRTH_YEAR')
                    }}
                />
                <FieldFeedback />
            </Fieldset>
        </>}
        <Field
            name="nationality"
            onChange={(next, { value, ...rest }) => {
                onNationalityChange(value, setFieldValue);
                next({ value, ...rest });
                Promise.resolve().then(() => validateField('permitNumber'));
            }}>
            <Label>{t('ICO.PERSONAL.NATIONALITY_LABEL')}</Label>
            <Select
                placeholder={t(COMMON_SELECT)}
                options={countryList}
                disabled={values.documentType === EEP}
                fullWidth
            />
            <FieldFeedback />
        </Field>
        {values.nationality === CHINA && <>
            <Field name="prcIdNumber">
                <Label>{t('ICO.PERSONAL.PRC_IDENTITY_NUMBER')}</Label>
                <TextInput fullWidth />
                <FieldFeedback />
            </Field>
            <Field name="chineseLegalName">
                <Label>{t('ICO.PERSONAL.CHINESE_LEGAL_NAME')}</Label>
                <TextInput fullWidth />
                <FieldFeedback />
            </Field>
        </>}
        {values.nationality !== CHINA && <Fieldset
            name="hasOtherNationality"
            marginBottom={3}
            onChange={(next, { value }) => {
                if (value === NO) {
                    setFieldValue('nationality2', '');
                    setFieldValue('nationality3', '');
                }
                linkTagging({
                    'page_url': TAG_PAGE_URL,
                    'page_language': lang,
                    'event_category': 'content',
                    'event_action': 'button',
                    'event_content': `do you hold any other nationality than chinese: ${value}`,
                    'page_name': TAG_PAGE_NAME,
                    'raw_datalayer': '5745v7'
                });
                next({ value });
            }}>
            <Label>{t('ICO.PERSONAL.NATIONALITY_QUESTION')}</Label>
            <YesNoRadio
                textMap={{ yes: t('ICO.COMMON.NO'), no: t('ICO.COMMON.YES') }}
                values={{ yes: NO, no: YES }}
            />
            <FieldFeedback />
        </Fieldset>}
        {values.nationality !== CHINA && values.hasOtherNationality === YES && <AddAnother
            itemInitialValue=""
            max={2}
            min={1}
            minOnMount={1}
            name="otherNationality"
            renderAddButtonText={() => t('ICO.PERSONAL.ADD_ANOTHER_NATIONALITY_ADD')}
            renderRemoveButtonText={() => t('ICO.PERSONAL.ADD_ANOTHER_NATIONALITY_REMOVE')}
            onAdd={onOtherNationalityAdd}
            onRemove={(event) => onOtherNationalityRemove(event, values, setFieldValue)}
            fullWidth
        >
            {({ path }) => (
                <Field name={getOtherNationalityFieldName(path)} marginBottom={0}>
                    <Label>{t('ICO.PERSONAL.NATIONALITY')}</Label>
                    <Paragraph id="nationality_desc" hint>{t('ICO.PERSONAL.NATIONALITY_DESC')}</Paragraph>
                    <Select
                        aria-describedby="nationality_desc"
                        placeholder={t('ICO.PERSONAL.NATIONALITY_PLACEHOLDER')}
                        options={getOtherNationalityOptions(countryList, values.nationality)}
                        fullWidth
                    />
                    <FieldFeedback />
                </Field>
            )}
        </AddAnother>}
    </Section>
);
